import type { FaturaNaLista } from '@arena/shared/schemas';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Botao, Quem, Status, Tabela, Tag } from '../ui/primitivos.jsx';
import { quemPaga, rotuloDaFatura, rotuloDaForma, SITUACAO_DA_FATURA } from './formatos.js';

/**
 * A tabela de faturas — "Faturas" de `#v-planos` e "Cobranças do mês" de
 * `#v-dash". As duas mostram a mesma linha; o painel acrescenta a forma.
 *
 * DUAS datas, e isso é o ponto: vencimento e pagamento são coisas
 * diferentes, e a tabela mostrava só a primeira. "O aluno pagou dia 12
 * uma fatura que vencia dia 5" é informação que a recepção precisa ver
 * sem abrir a fatura — é ela que explica a multa, e é por ela que se
 * reconhece quem paga sempre atrasado. Em aberto, a coluna mostra há
 * quantos dias está vencida.
 *
 * O botão da última coluna é o do protótipo: "Registrar pagamento" (ou
 * "Receber", no painel) na fatura aberta; a paga mostra a etiqueta. Clicar
 * na linha abre a fatura — para quem só lê, é a ficha dela.
 */
export function TabelaDeFaturas({
  faturas,
  comForma = false,
  rotuloDoBotao = 'Registrar pagamento',
  onAbrir,
}: {
  faturas: readonly FaturaNaLista[];
  comForma?: boolean;
  rotuloDoBotao?: string;
  onAbrir: (faturaId: string) => void;
}) {
  const { pode } = useSessao();
  const podeReceber = pode('cobrancas', 'registrar_pagamento');

  const colunas = [
    'Aluno',
    'Plano',
    'Vencimento',
    'Pagamento',
    'Valor',
    ...(comForma ? ['Forma'] : []),
    'Situação',
    '',
  ];

  return (
    <Tabela colunas={colunas}>
      {faturas.map((fatura) => {
        const aberta = fatura.situacao === 'EmAberto' || fatura.situacao === 'Vencida';
        return (
          <tr
            key={fatura.id}
            className="clickable"
            style={{ cursor: 'pointer' }}
            onClick={() => onAbrir(fatura.id)}
          >
            <td>
              <Quem nome={quemPaga(fatura)} />
            </td>
            <td>{fatura.plano.nome}</td>
            <td>{dataCurta(fatura.vencimento)}</td>
            <td>
              <DataDoPagamento fatura={fatura} />
            </td>
            <td className="num">{dinheiro(fatura.valorPago ?? fatura.valor)}</td>
            {comForma && <td>{rotuloDaForma(fatura.formaPagamento)}</td>}
            <td>
              <Status situacao={SITUACAO_DA_FATURA[fatura.situacao]}>
                {rotuloDaFatura(fatura)}
              </Status>
            </td>
            <td>
              {aberta && podeReceber ? (
                <Botao
                  variante="fantasma"
                  pequeno
                  onClick={(e) => {
                    e.stopPropagation();
                    onAbrir(fatura.id);
                  }}
                >
                  {rotuloDoBotao}
                </Botao>
              ) : fatura.situacao === 'Paga' ? (
                <Tag>{fatura.encargosRenunciados !== null ? 'paga · com renúncia' : 'paga'}</Tag>
              ) : null}
            </td>
          </tr>
        );
      })}
    </Tabela>
  );
}

/**
 * A coluna "Pagamento": o dia em que o dinheiro entrou.
 *
 * Paga depois do vencimento ganha o "+N" ao lado — é o atraso que
 * justifica a multa, e deixá-lo implícito obriga a recepção a subtrair
 * datas de cabeça. Em aberto, não há data: há dias de atraso correndo.
 */
function DataDoPagamento({ fatura }: { fatura: FaturaNaLista }) {
  if (fatura.pagoEm) {
    return (
      <>
        {dataCurta(fatura.pagoEm)}
        {fatura.diasDeAtraso > 0 && (
          <small style={{ color: 'var(--dim)' }}> · +{fatura.diasDeAtraso}d</small>
        )}
      </>
    );
  }
  if (fatura.situacao === 'Vencida') {
    return (
      <small style={{ color: 'var(--red-lt)' }}>
        {fatura.diasDeAtraso} {fatura.diasDeAtraso === 1 ? 'dia' : 'dias'} em atraso
      </small>
    );
  }
  return <span style={{ color: 'var(--dim)' }}>—</span>;
}
